/* eslint-disable import/prefer-default-export */
import _mapValues from 'lodash/mapValues';
import _omit from 'lodash/omit';
import _reduce from 'lodash/reduce';
import * as Papa from 'papaparse';
import store from 'store2';
import { getGrade } from '@bdelab/roar-utils';

const getGradeAndAgeForScoring = (scoringVersion = 3) => {
  let ageMonths = store.session.get('config').userMetadata?.ageMonths;
  let grade = getGrade(store.session.get('config').userMetadata?.grade);
  const { taskId } = store.session.get('config');

  // If age is not provided, we calculate it based on grade
  // Note: We use == instead of === because we want to catch both undefined and null.
  // eslint-disable-next-line eqeqeq
  if (ageMonths == undefined) {
    // eslint-disable-next-line eqeqeq
    if (grade == undefined) {
      throw new Error('Attempting to determine user age from grade but grade is undefined');
    }

    ageMonths = 66 + grade * 12;
  }

  const isAdaptive = scoringVersion >= 4;
  const ageMin = isAdaptive ? 60 : 48;
  const ageMax = isAdaptive ? 130 : 144;

  if (ageMonths < ageMin) ageMonths = ageMin;
  if (ageMonths > ageMax) ageMonths = ageMax;
  // Clamp grade to [1, 12] for v3 PA if < 1 or > 12. Otherwise, leave it unchanged.
  // eslint-disable-next-line eqeqeq
  if (grade != undefined && taskId === 'pa' && scoringVersion === 3) grade = Math.min(12, Math.max(1, grade));

  return {
    ageMonths,
    grade,
  };
};

const isValidForScoring = ({ ageMonths, grade, scoringVersion }) => {
  if (scoringVersion >= 3) {
    // For sre v4 & sre-es v1, we need the age, or we can estimate the age from the grade
    // eslint-disable-next-line eqeqeq
    return ageMonths != undefined || grade != undefined;
  }

  throw new Error('Invalid scoring version');
};

const useGradeForScoring = ({ scoringVersion, taskId }) => scoringVersion === 3 && taskId === 'pa';

const useAgeForScoring = ({ scoringVersion, taskId }) => scoringVersion >= 4 && taskId === 'pa';

export class RoarScores {
  constructor() {
    const rawScoringVersion = store.session.get('config').scoringVersion;
    this.scoringVersion = rawScoringVersion != null ? parseInt(rawScoringVersion, 10) : null;
    this.taskId = store.session.get('config').taskId;
    this.roarScoreKind = this.isAdaptiveScoring() ? 'scaled_irt' : 'raw_total_correct';
    this.useAgeForScoring = useAgeForScoring({ scoringVersion: this.scoringVersion, taskId: this.taskId });
    this.useGradeForScoring = useGradeForScoring({ scoringVersion: this.scoringVersion, taskId: this.taskId });
    this.tableURL = this.scoringVersion
      ? `https://storage.googleapis.com/roar-pa/scores/pa_lookup_v${this.scoringVersion}.csv`
      : null;
    this.lookupTable = [];
    this.tableLoaded = false;
    this.tableLoadingPromise = null;
    this.tableLoadingError = null;
    this.isValidForScoring = undefined;
    this.ageForScore = null;
    this.gradeForScore = null;
  }

  isAdaptiveScoring() {
    if (this.scoringVersion === null) {
      return false;
    }
    if (!Number.isFinite(this.scoringVersion)) {
      throw new Error(
        `Invalid scoringVersion: ${this.scoringVersion}. Expected a finite number >= 4 for adaptive scoring or < 4 for fixed scoring.`,
      );
    }
    return this.scoringVersion >= 4;
  }

  async initTable() {
    // Prevent race condition: if table is already loading, return the existing promise
    if (this.tableLoadingPromise) {
      return this.tableLoadingPromise;
    }

    // If table is already loaded, return immediately
    if (this.tableLoaded) {
      return Promise.resolve();
    }

    // Create and store the loading promise
    this.tableLoadingPromise = new Promise((resolve, reject) => {
      try {
        const { ageMonths, grade } = getGradeAndAgeForScoring(this.scoringVersion);

        if (!this.isAdaptiveScoring()) {
          this.isValidForScoring = isValidForScoring({
            ageMonths,
            grade,
            scoringVersion: this.scoringVersion,
          });
        } else {
          this.isValidForScoring = true;
        }

        if (!this.isValidForScoring) {
          reject(new Error('Invalid age or grade for scoring'));
          return;
        }

        this.ageForScore = ageMonths;
        this.gradeForScore = grade;

        Papa.parse(this.tableURL, {
          download: true,
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          step: (row) => {
            if (this.isAdaptiveScoring() && Number(this.ageForScore) === Number(row.data.ageMonths)) {
              // If adaptive, lookup scores by age only.
              this.lookupTable.push(_omit(row.data, ['', 'X']));
            } else if (this.gradeForScore && this.gradeForScore >= 6) {
              // Otherwise, lookup by grade if the user is in grade 6 or above.
              if (this.gradeForScore === Number(row.data.grade)) {
                this.lookupTable.push(_omit(row.data, ['', 'X']));
              }
            } else if (Number(this.ageForScore) === Number(row.data.ageMonths)) {
              // Otherwise, lookup by age in months.
              this.lookupTable.push(_omit(row.data, ['', 'X']));
            }
          },
          complete: () => {
            this.tableLoaded = true;
            this.tableLoadingPromise = null;
            resolve();
          },
          error: (error) => {
            this.tableLoadingPromise = null;
            // Reset to prevent stale data on retry
            this.lookupTable = [];
            reject(error);
          },
        });
      } catch (error) {
        this.tableLoadingPromise = null;
        reject(error);
      }
    });

    return this.tableLoadingPromise;
  }

  /**
   * This function calculates computed scores given raw scores for each subtask.
   *
   * The input raw scores are expected to conform to the following interface:
   *
   * interface IRawScores {
   *   [key: string]: {
   *     practice: ISummaryScores;
   *     test: ISummaryScores;
   *   };
   * }
   *
   * where the top-level keys correspond to this assessment's subtasks. If this
   * assessment has no subtasks, then there will be only one top-level key called
   * "composite." Each summary score object implements this interface:
   *
   * interface ISummaryScores {
   *   thetaEstimate: number | null;
   *   thetaSE: number | null;
   *   numAttempted: number;
   *   numCorrect: number;
   *   numIncorrect: number;
   * }
   *
   * The returned computed scores must have that same top-level keys as the input
   * raw scores, and each value must be an object with arbitrary computed scores.
   * For example, one might return the thetaEstimate, a "ROAR score", a percentile
   * score, and a predicted Woodcock-Johnson score:
   *
   * {
   *   fsm: {
   *     roarScore: x;
   *   },
   *   lsm: {
   *     roarScore: y;
   *   },
   *   del: {
   *     roarScore: z;
   *   },
   *   composite: {
   *     roarScore: x + y + z;
   *     standardScore: number;
   *     percentile: number;
   *     roarScoreKind: string;
   *     scoringVersion: number;
   *   },
   *   composite_foundational: {
   *     roarScore: x + y + z;
   *     standardScore: number;
   *     percentile: number;
   *     roarScoreKind: string;
   *     scoringVersion: number;
   *   }
   * }
   *
   * @param {*} rawScores
   * @returns {*} computedScores
   */
  computedScoreCallback = async (rawScores) => {
    const { taskId } = store.session.get('config');
    if (taskId !== 'pa') return null;

    // This returns an object with the same top-level keys as the input raw scores
    // But the values are the number of correct trials, not including practice trials.
    const computedScores = _mapValues(rawScores, (subtaskScores) => {
      const { numCorrect = 0, numAttempted = 0 } = subtaskScores.test ?? {};
      const percentCorrect = numAttempted > 0 ? Math.round((100 * numCorrect) / numAttempted) : 0;
      return {
        ...(this.isAdaptiveScoring() ? {} : { roarScore: numCorrect }),
        numCorrect,
        numAttempted,
        percentCorrect,
        roarScoreKind: this.roarScoreKind,
        scoringVersion: this.scoringVersion,
      };
    });

    // computedScores should now have keys for lsm, fsm, and del.
    // But we also want to update the total score so we add up all of the others.
    const totalScore = _reduce(
      _omit(computedScores, ['composite', 'composite_foundational']),
      (sum, score) => sum + score.roarScore,
      0,
    );

    // Composite raw counts are the sum across the subtasks (fsm/lsm/del). These are
    // emitted on the composite group so the score data matches the new platform, where
    // the backend best-run recompute reads numAttempted under domain='composite'.
    const subtaskScoresOnly = _omit(computedScores, ['composite', 'composite_foundational']);
    const compositeNumCorrect = _reduce(subtaskScoresOnly, (sum, score) => sum + (score.numCorrect ?? 0), 0);
    const compositeNumAttempted = _reduce(subtaskScoresOnly, (sum, score) => sum + (score.numAttempted ?? 0), 0);
    computedScores.composite = {
      ...computedScores.composite,
      numCorrect: compositeNumCorrect,
      numAttempted: compositeNumAttempted,
    };

    const thetas = store.session.get('thetas');
    const thetaSEs = store.session.get('thetaSEs');
    if (this.isAdaptiveScoring() && thetas && thetaSEs) {
      for (const key of Object.keys(computedScores)) {
        computedScores[key].thetaEstimate = thetas[key.toLowerCase()];
        computedScores[key].thetaSE = thetaSEs[key.toLowerCase()];
      }

      const compositeThetas = {
        thetaEstimate: thetas.scaled,
        thetaSE: thetaSEs.scaled,
        thetaEstimateRaw: thetas.composite,
        thetaSERaw: thetaSEs.composite,
      };

      const compositeFoundationalThetas = {
        thetaEstimate: thetas.composite_foundational,
        thetaSE: thetaSEs.composite_foundational,
        thetaEstimateRaw: thetas.composite_foundational,
        thetaSERaw: thetaSEs.composite_foundational,
      };

      computedScores.composite = {
        ...computedScores.composite,
        ...compositeThetas,
      };

      computedScores.composite_foundational = {
        ...computedScores.composite_foundational,
        ...compositeFoundationalThetas,
        roarScoreKind: this.roarScoreKind,
        scoringVersion: this.scoringVersion,
      };
    }

    const { userMetadata } = store.session.get('config');
    const grade = getGrade(userMetadata?.grade);
    const ageMonths = userMetadata?.ageMonths;
    const hasScoringVersion = this.scoringVersion != null;

    // eslint-disable-next-line eqeqeq
    if (hasScoringVersion && (grade != undefined || ageMonths != undefined)) {
      if (!this.tableLoaded) {
        try {
          // Subsequent calls await the same promise to avoid multiple network requests
          await this.initTable();
        } catch (error) {
          // Only log when error state changes to avoid flooding logs
          const errorMessage = error?.message || error;
          const previousErrorMessage = this.tableLoadingError?.message || this.tableLoadingError;
          if (previousErrorMessage !== errorMessage) {
            // eslint-disable-next-line no-console
            console.error('Error loading scoring table:', errorMessage);
            this.tableLoadingError = error;
          }
        }
      }

      // Then we find the row in the lookup table that corresponds to the total score.
      let myRow;

      if (this.isAdaptiveScoring() && thetas) {
        const thetaEstimate = thetas.scaled;
        const roundedTheta = Number(thetaEstimate.toFixed(1));
        myRow = this.lookupTable.find(
          (row) =>
            Number(row.ageMonths) === Number(this.ageForScore) &&
            Number(Number(row.thetaEstimate).toFixed(1)) === roundedTheta,
        );
      } else if (grade < 6) {
        myRow = this.lookupTable.find(
          (row) => Number(row.ageMonths) === Number(this.ageForScore) && row.roarScore === totalScore,
        );
      } else {
        myRow = this.lookupTable.find((row) => Number(row.grade) === grade && row.roarScore === totalScore);
      }

      if (myRow !== undefined) {
        // And add columns in the lookup table except for the age and roarScore.
        const { roarScore, ...normedScores } = _omit(myRow, ['ageMonths', 'grade']);

        computedScores.composite = {
          ...computedScores.composite,
          ...computedScores.composite_foundational,
          ...normedScores,
          // If adaptive, conditionally insert roarScore into the computed scores
          ...(this.isAdaptiveScoring() ? { roarScore } : {}),
          roarScoreKind: this.roarScoreKind,
          scoringVersion: this.scoringVersion,
        };
      }
    }

    return computedScores;
  };
}
